<template>
  <div
    v-if="canResync && hasAnyTimestamp"
    class="tw-fixed tw-z-[1099] tw-select-none"
    :style="{ left: position.left + 'px', top: position.top + 'px' }"
    data-testid="sync-details"
  >
    <div class="tw-relative tw-group">
      <button
        class="tw-w-12 tw-h-12 tw-rounded-full tw-bg-primary tw-text-white tw-flex tw-items-center tw-justify-center tw-shadow-spf-md hover:tw-bg-primary-600 focus:tw-bg-primary active:tw-bg-primary-700 focus:outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-offset-transparent focus:tw-ring-spf-primary tw-transition tw-cursor-grab"
        aria-label="Sync details"
        @click.stop.prevent="handleButtonClick"
        @mousedown="startDrag"
        @touchstart.passive="startDrag"
      >
        <i class="fa-solid fa-arrows-rotate tw-text-[18px]" />
      </button>

      <!-- Tooltip -->
      <div
        class="tw-absolute tw-left-14 tw-top-1/2 -tw-translate-y-1/2 tw-bg-neutral-900 tw-text-white tw-text-sm tw-font-medium tw-px-3 tw-py-1.5 tw-rounded tw-shadow tw-opacity-0 tw-scale-95 group-hover:tw-opacity-100 group-hover:tw-scale-100 tw-pointer-events-none tw-transition tw-whitespace-nowrap"
      >
        Sync details
      </div>

      <!-- Popover -->
      <div
        v-if="open"
        class="tw-absolute tw-bottom-16 tw-left-0 tw-bg-light tw-text-neutral-900 tw-rounded-md tw-shadow-spf-lg tw-w-[400px] tw-max-w-[90vw] tw-border tw-border-neutral-200 tw-px-4 tw-pt-4 tw-pb-12"
        @click.stop
      >
        <div v-if="reportBuiltAt" class="tw-text-sm tw-mb-3">
          <span class="tw-font-semibold">Report built at:</span>
          <span class="tw-ml-2">{{ reportBuiltAtFormatted }}</span>
        </div>
        <div v-if="reportLastReceivedSync" class="tw-text-sm">
          <span class="tw-font-semibold">Report last synced at:</span>
          <span class="tw-ml-1">{{ reportLastReceivedSyncFormatted }}</span>
        </div>
        <div class="tw-absolute tw-bottom-3 tw-right-3">
          <button
            v-if="canResync"
            :disabled="cooldownActive"
            :aria-disabled="cooldownActive ? 'true' : 'false'"
            class="tw-inline-flex tw-items-center tw-gap-2 tw-bg-transparent tw-text-primary tw-text-sm tw-font-semibold tw-px-2 tw-py-1 tw-rounded tw-cursor-pointer hover:tw-underline focus:outline-none focus:tw-bg-transparent focus:tw-text-primary focus:tw-no-underline active:tw-bg-transparent active:tw-text-primary disabled:tw-text-neutral-400 disabled:tw-cursor-not-allowed disabled:tw-no-underline"
            @click.stop="onResyncClick"
          >
            <i class="fa-solid fa-arrows-rotate" />
            <span v-if="!cooldownActive">Resync</span>
            <span v-else>Resync in {{ remainingSeconds }}s</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'SyncDetailsButton',
  data() {
    return {
      open: false,
      position: { left: 16, top: 16 },
      dragging: false,
      dragOffset: { x: 0, y: 0 },
      buttonSize: 48,
      dragStart: { x: 0, y: 0 },
      dragMoved: false,
      dragThreshold: 4,
      suppressClick: false,
      cooldownActive: false,
      cooldownTimer: null,
      cooldownEndsAt: 0,
      remainingSeconds: 0,
    }
  },
  computed: {
    ...mapState('reports', [
      'company',
      'inspection',
      'loggedIn',
      'report',
      'reportBuiltAt',
      'reportLastReceivedSync',
      'userProfile',
    ]),
    isAdmin() {
      return this.loggedIn && this.userProfile.type === 'admin'
    },
    isCompanyAllowed() {
      return (
        this.loggedIn &&
        this.userProfile.attributes.company_id ===
          this.inspection.attributes.company_id
      )
    },
    canResync() {
      return this.isCompanyAllowed || this.isAdmin
    },
    hasAnyTimestamp() {
      return !!(this.reportBuiltAt || this.reportLastReceivedSync)
    },
    reportBuiltAtFormatted() {
      if (!this.reportBuiltAt) return ''
      return new Date(this.reportBuiltAt).toLocaleString()
    },
    reportLastReceivedSyncFormatted() {
      if (!this.reportLastReceivedSync) return ''
      return new Date(this.reportLastReceivedSync).toLocaleString()
    },
    cooldownStorageKey() {
      try {
        const id = this.report && this.report.id
        return id ? `syncCooldownEnd:${id}` : null
      } catch (e) {
        return null
      }
    },
  },
  mounted() {
    // initial bottom-left placement
    this.position.top = window.innerHeight - this.buttonSize - 16
    this.position.left = 16
    window.addEventListener('resize', this.boundIntoViewport)
    document.addEventListener('mousemove', this.onDrag)
    document.addEventListener('mouseup', this.stopDrag)
    document.addEventListener('touchmove', this.onDrag, { passive: false })
    document.addEventListener('touchend', this.stopDrag)
    document.addEventListener('click', this.handleOutsideClick)
    document.addEventListener('keydown', this.onKeydown)

    // Restore cooldown from localStorage for this report
    try {
      const key = this.cooldownStorageKey
      const stored = key ? parseInt(localStorage.getItem(key), 10) : NaN
      if (!Number.isNaN(stored) && stored > Date.now()) {
        this.startCooldown(stored - Date.now())
      }
    } catch (e) {}
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.boundIntoViewport)
    document.removeEventListener('mousemove', this.onDrag)
    document.removeEventListener('mouseup', this.stopDrag)
    document.removeEventListener('touchmove', this.onDrag)
    document.removeEventListener('touchend', this.stopDrag)
    document.removeEventListener('click', this.handleOutsideClick)
    document.removeEventListener('keydown', this.onKeydown)
    if (this.cooldownTimer) clearInterval(this.cooldownTimer)
  },
  methods: {
    toggle() {
      this.open = !this.open
    },
    onKeydown(e) {
      if (e.key === 'Escape') this.open = false
    },
    startDrag(e) {
      this.dragging = true
      const point = this.pointFromEvent(e)
      this.dragOffset.x = point.x - this.position.left
      this.dragOffset.y = point.y - this.position.top
      this.dragStart = { x: point.x, y: point.y }
      this.dragMoved = false
    },
    onDrag(e) {
      if (!this.dragging) return
      e.preventDefault()
      const point = this.pointFromEvent(e)
      this.position.left = this.clamp(
        point.x - this.dragOffset.x,
        8,
        window.innerWidth - this.buttonSize - 8
      )
      this.position.top = this.clamp(
        point.y - this.dragOffset.y,
        8,
        window.innerHeight - this.buttonSize - 8
      )
      if (
        Math.abs(point.x - this.dragStart.x) > this.dragThreshold ||
        Math.abs(point.y - this.dragStart.y) > this.dragThreshold
      )
        this.dragMoved = true
    },
    stopDrag() {
      this.dragging = false
      if (this.dragMoved) {
        this.suppressClick = true
        setTimeout(() => (this.suppressClick = false), 150)
      }
    },
    handleOutsideClick(e) {
      if (!this.$el.contains(e.target)) this.open = false
    },
    handleButtonClick(e) {
      if (this.suppressClick || this.dragMoved) {
        this.dragMoved = false
        return
      }
      this.toggle()
    },
    onResyncClick() {
      if (this.cooldownActive) return
      this.$emit('quick-sync')
      this.startCooldown(60 * 1000)
    },
    startCooldown(durationMs) {
      this.cooldownActive = true
      this.cooldownEndsAt = Date.now() + durationMs
      // Persist so reloads keep the lockout
      try {
        const key = this.cooldownStorageKey
        if (key) localStorage.setItem(key, String(this.cooldownEndsAt))
      } catch (e) {}
      this.remainingSeconds = Math.ceil(durationMs / 1000)
      if (this.cooldownTimer) clearInterval(this.cooldownTimer)
      this.cooldownTimer = setInterval(() => {
        const remaining = Math.max(0, this.cooldownEndsAt - Date.now())
        this.remainingSeconds = Math.ceil(remaining / 1000)
        if (remaining <= 0) {
          this.cooldownActive = false
          clearInterval(this.cooldownTimer)
          this.cooldownTimer = null
          this.remainingSeconds = 0
          try {
            const key = this.cooldownStorageKey
            if (key) localStorage.removeItem(key)
          } catch (e) {}
        }
      }, 250)
    },
    pointFromEvent(e) {
      if (e.touches && e.touches[0]) {
        return { x: e.touches[0].clientX, y: e.touches[0].clientY }
      }
      return { x: e.clientX, y: e.clientY }
    },
    clamp(v, min, max) {
      return Math.min(Math.max(v, min), max)
    },
    boundIntoViewport() {
      this.position.left = this.clamp(
        this.position.left,
        8,
        window.innerWidth - this.buttonSize - 8
      )
      this.position.top = this.clamp(
        this.position.top,
        8,
        window.innerHeight - this.buttonSize - 8
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.sync-details-container :deep(.fa-solid) {
  font-size: 14px;
}
</style>
